import React from "react";

const PromotionSignalsVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson treats promotions like weather forecasting. It argues that opportunities and promotion decisions leave signals long before they are announced. Through Noel’s story, it shows how managers test successors by shifting meeting access and responsibilities. It highlights signals in money language, leadership attention, new buzzwords, and which topics go quiet. The aim is to read the pattern early and move your work toward where the company is headed.
      </p>

     
    </div>
  );

  const transcriptHtml = `
    <p>Have you ever wondered how weather forecasters predict it'll rain, and it actually does?</p>
    <p>Maybe they are psychic…? Or they just know how to <strong>read the signs</strong> before the rest of us do.</p>

    <p>Now imagine your company as the atmosphere.</p>
    <p><strong>Same principle.</strong></p>

    <p>There are signals that predict who's getting promoted next year. No crystal ball. No astrology.</p>
    <p>Just <strong>pattern recognition.</strong> And this is what you have to master.</p>

    <p>The corporate world runs on signals. And while you're busy doing your job, other people are busy doing signal analysis.</p>
    <p>And don't get me wrong, they're not smarter.</p>
    <p>They're just tuned into a different frequency: one that broadcasts in <strong>signals</strong></p>

    <p>The early warning signs were there weeks, sometimes months, before.</p>
    <p>You just didn't know how to read them.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Let's take Noel's case.</h3>
    <p>His manager suddenly started "shadowing" key meetings, smiling a bit too much, delegating a bit too freely, and dropping lines like "<em>You've got this covered, right?</em>"</p>
    <p>A week later, Noel noticed he was attending top-level visits and leadership lunches that never used to include him.</p>

    <p>Now, most people would think, "Oh, visibility!" But Noel read the signs.</p>
    <p>His manager wasn't being nice, he was <strong>succession planning</strong>.</p>

    <p>Because when a manager starts finding their own replacement, they won't say it outright. They'll just start testing who can handle the umbrella when the storm hits.</p>
    <p>Now, because Noel noticed these subtle changes, he started demonstrating his capabilities more visibly and he passed the test.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Let's look at a few of these subtle but strong weather changes.</h3>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">The language of money.</h3>
    <p>When leadership starts saying "<strong>investing in</strong>" instead of "cutting back on," the climate is shifting.</p>
    <p>When "savings" turns into "growth," and "realignment" magically becomes "<strong>strategic expansion</strong>", that's not optimism. That's funding.</p>

    <p><strong>Translation?</strong></p>
    <p>The company's about to rain opportunities, just not everywhere.</p>
    <p>So, if your project suddenly gets rebranded from "cost control" to "innovation initiative," congratulations. You've moved from drizzle to clear skies. If not, pack your bags and move to where it's sunny.</p>

    <p>If senior leaders are suddenly attending your team meetings, they're not there for moral support. They're <strong>scanning.</strong> They're mapping who's calm under pressure and who blames the brief.</p>

    <p>And if your boss starts looping you into "strategic syncs" or "cross-functional check-ins," that's code for: "<em>We're testing you for altitude.</em>"</p>

    <p>So stop thinking, "Why am I in this meeting?" You're in the meeting because <strong>someone's watching how you handle it</strong>.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Every company has its gossip.</h3>
    <p>Watch what people suddenly stop talking about. That's where the old weather's dying.</p>
    <p>Then notice what's getting all the airtime, the new initiative, pilot, or buzzword of the quarter. That's where the spotlight's moving.</p>

    <p>If the "Digital Transformation" Slack channel goes quiet, but suddenly "AI Enablement" is blowing up, guess where leadership's attention just migrated.</p>

    <p><strong>So… what do I do with all this forecasting?</strong></p>
    <p>Simple. Stop waiting for the tap on the shoulder. Start placing yourself where the company is already headed. Notice the weather changing instead of complaining about the rain.</p>

    <p><strong>Did you notice the opportunities around you?</strong></p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 text-body leading-7 text-[#484848] max-w-2xl"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default PromotionSignalsVideo;

